import { Help } from '@/components/Form'
import { Sentence, SentenceText } from '@/features/legends/components/Sentence'
import { ChoiceSelect } from '@/features/legends/components/ChoiceSelect'
import type { SelectOption } from '@/components/inputs/select-option'
import { MultiOptionSelect } from '@/features/legends/components/OptionSelect'
import type { ConjunctionOperator } from '@/features/legends/filters/rules'
import styles from './AllOrAnyOfSelect.module.css'

export interface AllOrAnyOfSelectProps {
  /**
   * Who has the options, as in "Pro has all of".
   */
  subject: string
  /**
   * What the options are, as in "the following skills".
   */
  noun: string
  op: ConjunctionOperator
  onOpChange: (op: ConjunctionOperator) => unknown
  options: readonly SelectOption[]
  value: readonly string[]
  onChange: (ids: string[]) => unknown
  placeholder: string
  error?: string
  isDisabled?: boolean
  'aria-label': string
  testId?: string
  optionsTestId?: string
}

/**
 * Picks options that all, or any, of must be had: "[Pro has all of] the
 * following skills [skills]".
 */
export function AllOrAnyOfSelect({
  subject,
  noun,
  op,
  onOpChange,
  options,
  value,
  onChange,
  placeholder,
  error,
  isDisabled,
  'aria-label': ariaLabel,
  testId,
  optionsTestId,
}: AllOrAnyOfSelectProps) {
  return (
    <div className={styles.root}>
      <Sentence>
        <ChoiceSelect
          value={op}
          choices={[
            ['All', `${subject} has all of`],
            ['Any', `${subject} has any of`],
          ]}
          onChange={onOpChange}
          isDisabled={isDisabled}
          size="small"
          aria-label={ariaLabel}
          testId={testId && `${testId}-operation`}
        />
        <SentenceText>{`the following ${noun}`}</SentenceText>
      </Sentence>
      <MultiOptionSelect
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isInvalid={!!error}
        testId={optionsTestId}
      />
      <Help error={error} />
    </div>
  )
}
